"use client";

import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import NoPropsImage from "@/assets/images/account/no-qualideal.webp"; // Placeholder for actual image
import { PopLoading } from "@/components/pop-ui/pop-loading";
import { PullToRefreshWrapper } from "@/components/ui/pull-to-refresh";
import { useIsMobile } from "@/hooks/use-mobile";
import { getCurrentLanguageInClient, getValueFromTranslations } from "@/lib/utils.client";
import PropModal from "@/modules/activity/prop/components/prop-modal";
import UserPropItem, { type UserPropWithDefinition } from "@/modules/activity/prop/components/user-prop-item";
import { UserPropStatus } from "@/modules/activity/prop/schema";
import { getImageUrlsFromBanner } from "@/modules/order/utils";
import { useORPC } from "@/providers/orpc-provider";
import Empty from "./components/empty";
import { ModulePagination } from "./components/module-pagination";
import { ModuleTabs } from "./components/module-tabs";
import { ModuleTitle } from "./components/module-title";
export function RewardsModule(t0) {
  const $ = _c(39);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    onHeaderContentChange
  } = t1;
  const {
    t
  } = useTranslation();
  const orpc = useORPC();
  const isMobile = useIsMobile();
  const [activeTab, setActiveTab] = useState("available");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedUserProp, setSelectedUserProp] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  let t2;
  if ($[2] !== activeTab || $[3] !== currentPage || $[4] !== orpc.prop.userProp.findUserPropsWithDetails) {
    t2 = orpc.prop.userProp.findUserPropsWithDetails.queryOptions({
      input: {
        propType: "eligibility",
        status: activeTab === "available" ? UserPropStatus.AVAILABLE : "unavailable",
        page: currentPage,
        pageSize: 20
      }
    } as any);
    $[2] = activeTab;
    $[3] = currentPage;
    $[4] = orpc.prop.userProp.findUserPropsWithDetails;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t2) {
    t3 = {
      ...t2
    };
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const {
    data: propsResponse,
    isLoading,
    isError,
    refetch
  } = useQuery(t3);
  const userProps = ((propsResponse as any)?.items || []) as unknown as UserPropWithDefinition[];
  const totalPages = (propsResponse as any)?.totalPages || 1;
  const lang = getCurrentLanguageInClient();
  let t4;
  if ($[8] !== refetch) {
    t4 = async () => {
      await refetch();
    };
    $[8] = refetch;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const handleRefresh = t4;
  let t5;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = userProp => {
      setSelectedUserProp(userProp);
      setIsModalOpen(true);
    };
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const handleViewDetails = t5;
  let t6;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      setIsModalOpen(false);
      setSelectedUserProp(null);
    };
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const handleCloseModal = t6;
  let t7;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = page => {
      setCurrentPage(page);
    };
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const handlePageChange = t7;
  let t8;
  if ($[13] !== refetch) {
    t8 = () => {
      setCurrentPage(1);
      refetch();
    };
    $[13] = refetch;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== activeTab) {
    t9 = [activeTab];
    $[15] = activeTab;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  useEffect(t8, t9);
  let t10;
  let t11;
  if ($[17] !== activeTab || $[18] !== isMobile || $[19] !== onHeaderContentChange || $[20] !== t) {
    t10 = () => {
      if (isMobile && onHeaderContentChange) {
        const headerContent = activeTab === "available" ? <button type="button" onClick={() => setActiveTab("history")} className="text-sm font-medium text-hint underline">{t("account.rewards.tabs.history")}</button> : <button type="button" onClick={() => setActiveTab("available")} className="text-sm font-medium text-hint underline">{t("account.rewards.back", "Back")}</button>;
        onHeaderContentChange(headerContent);
      }
    };
    t11 = [activeTab, isMobile, onHeaderContentChange, t];
    $[17] = activeTab;
    $[18] = isMobile;
    $[19] = onHeaderContentChange;
    $[20] = t;
    $[21] = t10;
    $[22] = t11;
  } else {
    t10 = $[21];
    t11 = $[22];
  }
  useEffect(t10, t11);
  let t12;
  if ($[23] !== t) {
    t12 = t("account.rewards.tabs.available");
    $[23] = t;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== t12) {
    t13 = {
      key: "available" as const,
      label: t12
    };
    $[25] = t12;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== t) {
    t14 = t("account.rewards.tabs.history");
    $[27] = t;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== t14) {
    t15 = {
      key: "history" as const,
      label: t14
    };
    $[29] = t14;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== t13 || $[32] !== t15) {
    t16 = [t13, t15];
    $[31] = t13;
    $[32] = t15;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  const TABS = t16;
  let t17;
  if ($[34] !== TABS || $[35] !== activeTab || $[36] !== isMobile || $[37] !== t) {
    t17 = !isMobile && <><ModuleTitle title={t("account.rewards.title", "\u8D5A\u53D6\u7684\u5956\u52B1")} /><ModuleTabs tabs={TABS} activeKey={activeTab} onChange={key => setActiveTab(key as "available" | "history")} /></>;
    $[34] = TABS;
    $[35] = activeTab;
    $[36] = isMobile;
    $[37] = t;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  const desktopHeaderDom = t17;
  const contentDom = () => {
    if (isLoading) {
      return <div className={isMobile ? "min-h-screen" : "min-h-[500px]"}>{desktopHeaderDom}<div className={`flex items-center justify-center py-8 ${isMobile ? "px-3" : ""}`}><PopLoading /></div></div>;
    }
    if (userProps.length === 0 || isError) {
      return <div className={isMobile ? "min-h-screen" : ""}>{desktopHeaderDom}<div className={isMobile ? "px-3 py-4" : ""}><Empty src={NoPropsImage} title={t("account.rewards.emptyText", {
            status: activeTab === "available" ? t("account.rewards.tabs.available") : t("account.rewards.tabs.history")
          })} /></div></div>;
    }
    return <>{desktopHeaderDom}<div className="w-full"><div className="grid grid-cols-1 items-start gap-4 md:grid-cols-2">{userProps.map(userProp_0 => <UserPropItem key={userProp_0.id} userProp={userProp_0} onViewDetails={handleViewDetails} />)}</div></div><PropModal isOpen={isModalOpen} onClose={handleCloseModal} name={selectedUserProp?.spuDetail?.name_trans ? getValueFromTranslations(lang, selectedUserProp.spuDetail.name_trans) : ""} images={selectedUserProp?.spuDetail?.bannerImages ? getImageUrlsFromBanner(lang, selectedUserProp.spuDetail.bannerImages) : []} howToRedeemHtml={selectedUserProp?.extraData?.description_trans ? getValueFromTranslations(lang, selectedUserProp.extraData.description_trans) : null} description={selectedUserProp?.propDefinition?.description_trans ? getValueFromTranslations(lang, selectedUserProp.propDefinition.description_trans) : null} /><div className={isMobile ? "px-3 pb-4" : ""}><ModulePagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} /></div></>;
  };
  return isMobile ? <div className="min-h-screen"><PullToRefreshWrapper onRefresh={handleRefresh}>{contentDom()}</PullToRefreshWrapper></div> : <div className={isMobile ? "min-h-screen" : ""}>{contentDom()}</div>;
}